import type { Topic } from '@/@types/topic'
import {
	filterLinkAttachments,
	filterPicAttachments,
	filterVideoAttachments,
} from '@/components/Attachment/utils'
import {
	AttachmentStatus,
	AttachmentType,
	FileAttachment,
	LinkInfoSchema,
	type Attachment,
} from '@/states/attachment'
import { compact, isEmpty } from 'lodash-es'
import { z } from 'zod'
import { validateVideo } from './utils'

const AttachmentSchema = z
	.custom<Attachment>()
	.refine(
		(attachment) => {
			if (attachment.type === AttachmentType.PICTURE) {
				const file = (attachment as FileAttachment).file
				if (file) {
					return file.size <= 20 * 1024 * 1024
				}
			}
			return true
		},
		{
			message: '图片体积不能超过 20MB',
		}
	)
	.superRefine(async (attachment, ctx) => {
		if (attachment.type === AttachmentType.VIDEO) {
			await validateVideo(attachment as FileAttachment, ctx)
		}
	})

const AttachmentsSchema = z
	.array(AttachmentSchema)
	.refine(
		(attachments) => {
			if (
				attachments.filter((item) => item.type === AttachmentType.PICTURE).length > 0 &&
				attachments.filter((item) => item.type === AttachmentType.VIDEO).length > 0
			) {
				return false
			}
			return true
		},
		{
			message: '不能同时添加图片和视频',
		}
	)
	.refine(
		(attachments) => {
			const pictures = attachments.filter((item) => item.type === AttachmentType.PICTURE)
			return pictures.length <= 9
		},
		{
			message: '最多添加9张图片',
		}
	)
	.refine(
		(attachments) => {
			return attachments.filter((item) => item.type === AttachmentType.VIDEO).length <= 1
		},
		{
			message: '最多添加1个视频',
		}
	)

export const InPayloadAttachmentsSchema = AttachmentsSchema.refine(
	(attachments) =>
		attachments.every((item) => {
			if (item.type === AttachmentType.LINK) {
				return true
			}
			return (
				[AttachmentType.PICTURE, AttachmentType.VIDEO].includes(item.type) &&
				(item as FileAttachment).status === AttachmentStatus.UPLOADED
			)
		}),
	{
		message: '请等待所有附件上传完成',
	}
)

// 定义表单验证 schema
export const PostFormSchema = z.object({
	// 帖子内容验证
	content: z.string(),

	// 附件验证
	attachments: AttachmentsSchema,

	// 话题验证
	topic: z.custom<Topic>().optional(),

	// ID 用于区分创建/编辑模式
	id: z.string().optional(),
})

// 导出类型
export type PostFormValue = z.infer<typeof PostFormSchema>

export const CreatePostPayloadSchema = z
	.object({
		coord: z
			.object({
				coordType: z.string(),
				lat: z.string(),
				lng: z.string(),
			})
			.optional(),
		pictureKeys: z.array(z.string()).optional(),
		videoKey: z.string().optional(),
		linkInfo: z
			.object({
				supplementary: z.boolean(),
				urlsInText: z.array(z.string()),
				linkType: z.literal('default'),
			})
			.merge(LinkInfoSchema)
			.optional(),
		submitToTopic: z.string().optional(),
		syncToPersonalUpdate: z.boolean(),
		attachments: InPayloadAttachmentsSchema,
	})
	.merge(
		PostFormSchema.pick({
			content: true,
			topic: true,
		})
	)
	.transform(({ content, attachments, topic, syncToPersonalUpdate }, ctx) => {
		const pictureKeys = compact(filterPicAttachments(attachments).map((item) => item.cdn?.file.key))
		const videoKey = filterVideoAttachments(attachments)[0]?.cdn?.file?.key
		const linkInfo = compact(filterLinkAttachments(attachments).map((item) => item.linkInfo))[0]

		if (!isEmpty(videoKey) && isEmpty(content)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: '请输入文字后发布',
				path: ['content'],
			})
		}

		return {
			content,
			pictureKeys,
			videoKey,
			linkInfo: linkInfo
				? {
						...linkInfo,
						supplementary: false,
						urlsInText: [],
						linkType: 'default',
					}
				: undefined,
			submitToTopic: topic?.id,
			syncToPersonalUpdate,
		}
	})

export type CreatePostPayload = z.infer<typeof CreatePostPayloadSchema>

const EditTypeSchema = z.enum(['IMAGE', 'VIDEO', 'LINK', 'TEXT'])
type EditTypeType = z.infer<typeof EditTypeSchema>

export const UpdatePostPayloadSchema = z
	.object({
		id: z.string(),
		editType: EditTypeSchema.optional(),
		pictureKeys: z.array(z.string()).optional(),
		videoKey: z.string().optional(),
		linkInfo: z
			.object({
				supplementary: z.boolean(),
				urlsInText: z.array(z.string()),
				linkType: z.literal('default'),
			})
			.merge(LinkInfoSchema)
			.optional(),
		submitToTopic: z.string().optional(),
		attachments: InPayloadAttachmentsSchema,
	})
	.merge(
		PostFormSchema.pick({
			content: true,
			topic: true,
		})
	)
	.transform(({ content, attachments, topic, id }, ctx) => {
		const pictureKeys = compact(filterPicAttachments(attachments).map((item) => item.cdn?.file.key))
		const video = filterVideoAttachments(attachments)[0]?.cdn?.file
		const videoKey = video?.key
		const linkInfo = compact(filterLinkAttachments(attachments).map((item) => item.linkInfo))[0]
		let editType: EditTypeType | undefined = undefined

		switch (true) {
			case !!video?.fileUrl:
				editType = EditTypeSchema.enum.VIDEO
				break
			case !isEmpty(pictureKeys):
				editType = EditTypeSchema.enum.IMAGE
				break
			case !!linkInfo:
				editType = EditTypeSchema.enum.LINK
				break
			default:
				editType = EditTypeSchema.enum.TEXT
				break
		}

		return {
			id,
			editType,
			content,
			pictureKeys,
			videoKey,
			linkInfo: linkInfo
				? {
						...linkInfo,
						supplementary: false,
						urlsInText: [],
						linkType: 'default',
					}
				: undefined,
			submitToTopic: topic?.id,
		}
	})

export type UpdatePostPayload = z.infer<typeof UpdatePostPayloadSchema>
